import { ExpenseReportDetail, ExpenseReportHeader, getExpensesForWeekEnding, postExpenses, putExpenses } from './ExpensesAPI';
import { getUTCDateString } from '../../utils/Functions';
import { cloneDeep, isEqual } from 'lodash';
import { CYMAJob, getCostCodesforJob } from '../../utils/CYMAAPI';
import { Employee } from '../../utils/AccountsAPI';
import { CancelToken } from 'axios';
import { getWeekEndingDate, parseStringNumber } from '../../utils/TSFunctions';
let dateFormat = require('dateformat'); //formats dates very well

/*
    Name: ExpenseReport
    Author: Tyler Kennedy
    Date: 9/24/2020
    Purpose: Expene Report Middleware
*/

/*
------------------------------------------------------
------------------ExpenseReport Class-----------------
------------------------------------------------------
*/

export default class ExpenseReport {
  _header: ExpenseReportHeader;
  _dateCreated: Date;
  _weekEnding: Date;
  _employeeInfo: Employee;
  jobs: CYMAJob[] = [];
  schedulerJobs: CYMAJob[] = [];

  constructor(employeeInfo: Employee, weekEnding: Date, jobs: CYMAJob[] = [], schedulerJobs: CYMAJob[] = []) {
    this._header = this.emptyExpenseReport();
    this._header.periodEndingDate = weekEnding;
    this._weekEnding = weekEnding;
    this.weekEnding = weekEnding;
    this._dateCreated = new Date();
    this._employeeInfo = employeeInfo;
    this.employeeInfo = employeeInfo;
    this.jobs = jobs;
    this.schedulerJobs = schedulerJobs;
  }

  set weekEnding(weekEnding: Date) {
    if (weekEnding !== this._weekEnding) {
      this._header.periodEndingDate = weekEnding;
      this._weekEnding = getWeekEndingDate(weekEnding);
    }
  }

  get weekEnding(): Date {
    return this._weekEnding;
  }

  set employeeInfo(employeeInfo: Employee) {
    this._header.userId = employeeInfo.id;
    this._employeeInfo = employeeInfo;
  }

  get employeeInfo(): Employee {
    return this._employeeInfo;
  }

  set data(expenseReport) {
    expenseReport.userId = expenseReport.userId ?? this._employeeInfo.id;
    expenseReport.periodEndingDate = expenseReport.periodEndingDate ?? this.getWeekEndingAsString();
    this._header = expenseReport;
  }

  get data() {
    return this._header;
  }

  get header() {
    return cloneDeep(this.data);
  }

  get locked() {
    return this._header.managerApprovedBy || this._header.accountingApprovedBy || this.isExpired ? true : false;
  }

  get isExpired() {
    return (new Date().getTime() - this._weekEnding?.getTime()) / (1000 * 60 * 60 * 24) > 21;
  }

  get isManagerApproved() {
    return this._header.managerApprovedBy ? true : false;
  }

  get isAccountingApproved() {
    return this._header.accountingApprovedBy ? true : false;
  }

  get totalExpenses() {
    return this._header.details.reduce((sum, d) => sum + (d.expenseAmount ?? 0), 0);
  }

  get objType() {
    return 'expenses';
  }

  get errors() {
    return this.getErrors();
  }

  getErrors(validateJob = true, validateCostCode = true): string[] {
    let errorMessages: string[] = [];

    this._header.details.forEach((detail, i) => {
      if (isEqual(detail, this.emptyDetail())) return;

      if (isNaN(detail.expenseAmount ?? NaN) || (detail.expenseAmount ?? 0) <= 0)
        errorMessages.push('Invailid Expense Amount [ ' + (detail.expenseAmount ?? 0) + ' ] row: ' + (i + 1));

      if (!detail.jobNumber || detail.jobNumber === '') errorMessages.push('Invalid Job Number [ ' + detail.jobNumber + ' ] row: ' + (i + 1));

      if (!detail.placeOfPurchase || detail.placeOfPurchase === '') errorMessages.push('Merchant Cannot be Empty [ ' + detail.placeOfPurchase + ' ] row: ' + (i + 1));

      if (
        (detail?.dateOfPurchase ?? '') > this._weekEnding ||
        (this._header.periodEndingDate && (detail?.dateOfPurchase ?? '') > this._header.periodEndingDate) ||
        !detail.dateOfPurchase
      )
        errorMessages.push('Invalid date: [' + getUTCDateString(detail.dateOfPurchase) + ' ] row ' + (i + 1));

      if (this.typesOfExpense().findIndex((types) => types.name === detail.expenseType) === -1)
        errorMessages.push('Invalid Expense Type: [' + detail.expenseType + ' ] row ' + (i + 1));

      let job = this.jobs.find((j) => j.jobNumber === detail.jobNumber);
      let schedulerJob = this.schedulerJobs.find((j) => j.jobNumber === detail.jobNumber);
      let ccFound =
        (job?.costCodes?.findIndex((c) => c.code === detail.costCode) ?? -1) > -1 || (schedulerJob?.costCodes?.findIndex((c) => c.code === detail.costCode) ?? -1) > -1;
      if (validateCostCode && !ccFound) errorMessages.push('Invalid Cost Code: [' + detail.costCode + '] row ' + (i + 1));
    });

    return errorMessages;
  }

  static detailHasErrors(detail: ExpenseReportDetail, expense: ExpenseReport): boolean {
    return (
      !isEqual(detail, this.emptyDetail()) || //empty is no error
      isNaN(detail.expenseAmount ?? NaN) || //NaN expense amount
      (detail.expenseAmount ?? 0) <= 0 || //negative expense amount
      !detail.jobNumber || //no job
      detail.jobNumber === '' || //empty job
      !detail.placeOfPurchase || //no merchant
      detail.placeOfPurchase === '' || //empty merchant
      (detail?.dateOfPurchase ?? '') > expense._weekEnding || //future purchase
      (expense._header.periodEndingDate && (detail?.dateOfPurchase ?? '') > expense._header.periodEndingDate) || //future purchase
      !detail.dateOfPurchase || //no date
      this.typesOfExpense().findIndex((types) => types.name === detail.expenseType) === -1 || //not valid type of expense
      (detail.costCode !== 'ALL' && !((detail.costCodes?.findIndex((code) => code.code === detail.costCode) ?? -1) > -1)) //bad cost code
    );
  }

  copy(): ExpenseReport {
    return cloneDeep(this);
  }

  updateHeader(fieldId: string, value: any): ExpenseReport {
    let clone = cloneDeep(this);
    if (clone._header[fieldId] !== value) {
      clone._header[fieldId] = value;
    }
    return clone;
  }

  newDetail(): ExpenseReport {
    let clone = cloneDeep(this);
    clone._header.details.push(clone.emptyDetail());
    return clone;
  }

  withEmptyDetail(): ExpenseReport {
    var clone = cloneDeep(this);
    var existingIndex = clone._header.details.findIndex((detail) => !detail.id);
    if (existingIndex > -1) return clone;
    else {
      clone._header.details.push(clone.emptyDetail());
      return clone;
    }
  }

  removeDetail(rowNumber: number): ExpenseReport {
    let clone = cloneDeep(this);
    let header = clone._header;
    header.details.splice(rowNumber, 1);
    if (header.details.length === 0) {
      header.details = [clone.emptyDetail()];
    }
    return clone;
  }

  updateCostCodes(rowNum: number, token?: CancelToken): Promise<ExpenseReport> {
    let clone = cloneDeep(this);
    let promise: Promise<any> | null = null;
    let detail = clone._header.details[rowNum];

    if (detail.jobNumber && detail.jobNumber !== '') {
      if (detail.jobNumber !== (detail.costCodes[0] ? detail.costCodes[0].jobNumber : '')) {
        let job = clone.jobs?.find((job) => job.jobNumber === detail.jobNumber) ?? clone.schedulerJobs?.find((job) => job.jobNumber === detail.jobNumber);
        if ((job?.costCodes?.length ?? 0) > 0) promise = Promise.resolve(job?.costCodes);
        else promise = getCostCodesforJob(detail.jobNumber, token);
      }
    } else {
      detail.costCodes = [];
      detail.costCode = undefined;
    }

    if (promise)
      return promise.then((response) => {
        let detail = clone._header.details[rowNum];
        if (detail) {
          detail.costCodes = response;
          if (detail.costCodes?.length === 1) detail.costCode = detail.costCodes[0].code;
        }
        return clone;
      });
    else {
      return Promise.resolve(clone);
    }
  }

  static updateDetailToNewJob(detail: ExpenseReportDetail, expenseReport: ExpenseReport, jobs?: CYMAJob[]): ExpenseReportDetail {
    let clone = cloneDeep(expenseReport);

    //if valid job number
    if (detail.jobNumber && detail.jobNumber !== '') {
      if (detail.jobNumber !== (detail.costCodes[0] ? detail.costCodes[0].jobNumber : '')) {
        let job = (jobs ?? clone.jobs)?.find((job) => job.jobNumber === detail.jobNumber) ?? clone.schedulerJobs?.find((job) => job.jobNumber === detail.jobNumber);
        detail.costCodes = job?.costCodes ?? [];
        if (detail.costCodes?.length === 1) detail.costCode = detail.costCodes[0]?.code;
        else detail.costCode = undefined;
      }
    } else {
      detail.costCodes = [];
      detail.costCode = undefined;
    }
    return detail;
  }

  // used to be updateReportDetail
  updateField(rowNum: number, fieldId: string, newValue: any, token?: CancelToken): Promise<ExpenseReport> {
    let clone = cloneDeep(this);
    const value = newValue || '';

    //no update
    if (clone._header.details[rowNum][fieldId] === value) {
      return Promise.resolve(clone);
    }

    clone._header.details[rowNum][fieldId] = value;

    if (fieldId === 'jobNumber') {
      return clone.updateCostCodes(rowNum, token);
    } else if (fieldId === 'expenseAmount') {
      let valueForTest = newValue;
      valueForTest = valueForTest.replace('$', '');
      if (isNaN(valueForTest) || valueForTest === null) {
        //could need more error checking isNaN
        valueForTest = 0;
      } else {
        valueForTest = parseStringNumber(valueForTest);
      }

      clone._header.details[rowNum][fieldId] = valueForTest; //may not be needed

      return Promise.resolve(clone);
    } else {
      return Promise.resolve(clone);
    }
  }

  updateApprovals(approvalType: 'Accounting' | 'Manager', approvedBy: string | undefined) {
    let clone = cloneDeep(this);
    if (approvalType === 'Accounting') {
      clone._header.accountingApprovedBy = approvedBy;
      clone._header.accountingApprovedDate = approvedBy ? new Date() : undefined;
    } else if (approvalType === 'Manager') {
      clone._header.managerApprovedBy = approvedBy;
      clone._header.managerApprovedDate = approvedBy ? new Date() : undefined;
    }
    return clone;
  }

  getApprovals(approvalType: 'Accounting' | 'Manager') {
    if (approvalType === 'Accounting') {
      return this._header.accountingApprovedBy;
    } else if (approvalType === 'Manager') {
      return this._header.managerApprovedBy;
    } else return false;
  }

  //formated returns
  getWeekEndingAsString() {
    return getUTCDateString(this._weekEnding);
  }

  getGridTotals() {
    let totalDetail = this.emptyDetail();
    totalDetail.dateOfPurchase = 'Totals:';
    totalDetail.expenseAmount = this._header.details?.reduce((sum, detail) => sum + (detail.expenseAmount ?? 0), 0);
    return totalDetail;
  }

  getTotalExpenses() {
    return this._header.details?.reduce((sum, detail) => sum + (detail.expenseAmount ?? 0), 0);
  }

  //API CALLS
  getAPIExpenseReport(token?: CancelToken): Promise<ExpenseReport> {
    if (this._employeeInfo && this._weekEnding) {
      const promise = getExpensesForWeekEnding(this.employeeInfo.id, this.getWeekEndingAsString(), token);
      return promise
        .then((response) => {
          let clone = cloneDeep(this);
          if (response.details.length === 0) {
            response.details = [clone.emptyDetail()];
          }
          clone.data = response;
          return clone;
        })
        .catch((err) => {
          let clone = cloneDeep(this);
          if (err?.response?.status !== 404) {
            throw err;
          }

          clone.data = clone.emptyExpenseReport();
          return Promise.resolve(clone);
        });
    } else {
      return Promise.reject('Expense Report is missing' + (this.employeeInfo ? '' : 'Employee Info') + (this._weekEnding ? '' : 'Week Ending Data'));
    }
  }

  save(validateJob = true, validateCostCode = true, validate = true): Promise<ExpenseReport> {
    let clone = cloneDeep(this);
    clone.data.details = clone.data.details.filter((detail) => {
      return !isEqual(detail, clone.emptyDetail());
    });
    if (!validate || clone.getErrors(validateJob, validateCostCode).length === 0) {
      if (clone.data.id) {
        return putExpenses(clone.data).then((response) => {
          clone.data = response;
          if (clone.data.details.length === 0) clone.data.details = [clone.emptyDetail()];
          return clone;
        });
      } else {
        return postExpenses(clone.data).then((response) => {
          clone.data = response;
          if (clone.data.details.length === 0) clone.data.details = [clone.emptyDetail()];
          return clone;
        });
      }
    } else {
      return Promise.reject('Expense report not saved. Fix data errors before saving.');
    }
  }

  //empty Data
  emptyExpenseReport(): ExpenseReportHeader {
    return ExpenseReport.emptyExpenseReport(this._dateCreated ?? undefined);
  }

  emptyDetail() {
    return ExpenseReport.emptyDetail(this._dateCreated ?? undefined);
  }

  typesOfExpense() {
    return ExpenseReport.typesOfExpense();
  }

  //static versions of functions
  static emptyExpenseReport(date = new Date()): ExpenseReportHeader {
    return {
      id: undefined,
      userId: undefined,
      user: undefined,
      periodEndingDate: undefined,
      managerApprovedBy: undefined,
      managerApprovedDate: undefined,
      accountingApprovedBy: undefined,
      accountingApprovedDate: undefined,
      lastExportedDate: undefined,
      details: [this.emptyDetail(date)],
      lastUpdatedBy: undefined,
      lastUpdatedDate: undefined,
      createdBy: undefined,
      createdDate: dateFormat(date, "yyyy-mm-dd'T'HH:MM:ss"),
    };
  }

  static emptyDetail(date = new Date()): ExpenseReportDetail {
    return {
      id: undefined,
      headerId: undefined,
      dateOfPurchase: undefined,
      placeOfPurchase: undefined,
      expenseType: undefined,
      expenseAmount: undefined,
      jobNumber: undefined,
      costCode: undefined,
      costCodes: [],
      comments: undefined,
      header: undefined,
      files: [],
      lastUpdatedBy: undefined,
      lastUpdatedDate: undefined,
      createdBy: undefined,
      isPayable: true,
      createdDate: dateFormat(date, "yyyy-mm-dd'T'HH:MM:ss"),
    };
  }

  static typesOfExpense() {
    return [
      {
        name: 'Airfare',
      },
      {
        name: 'Rental Car',
      },
      {
        name: 'Fuel',
      },
      {
        name: 'Lodging',
      },
      {
        name: 'Meals',
      },
      {
        name: 'Mileage',
      },
      {
        name: 'Boot Reimbursment',
      },
      {
        name: 'Reimb. Exp. on Top',
      },
      {
        name: 'Travel',
      },
      {
        name: 'Medical Reimbursment',
      },
      {
        name: 'Insurance',
      },
      {
        name: 'Other',
      },
    ];
  }
}
